import { practiceEmptyStateCopy } from "../practice-lens-ui";

/**
 * Redesign · feed states (sealed mock).
 *
 * Empty and loading are the mock's. The error state is not depicted in the mock
 * (a static reference has no failure path); it reuses the empty-card material
 * with a Retry so a failed fetch is still handled honestly.
 */

export function FeedEmptyState({
  onClearFilters,
  practiceDisplayName = null,
}: {
  onClearFilters: () => void;
  /** Set only when a selected practice's own total is genuinely zero (2W) —
   *  renders the explicit "not yet routed" copy instead of the ordinary
   *  filter-mismatch copy. Never implies no opportunities exist firm-wide. */
  practiceDisplayName?: string | null;
}): JSX.Element {
  const copy = practiceDisplayName
    ? practiceEmptyStateCopy(practiceDisplayName)
    : null;
  return (
    <div className="sx-empty">
      <svg
        width="34"
        height="34"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <h2>{copy ? copy.title : "No signals match these filters"}</h2>
      <p>
        {copy
          ? copy.body
          : "Widen the signal type or status filters, or clear the search, and the feed will fill back in."}
      </p>
      <button type="button" className="sx-dbtn sx-dter" onClick={onClearFilters}>
        Clear filters
      </button>
    </div>
  );
}

export function FeedErrorState({
  onRetry,
}: {
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="sx-empty">
      <h2>Signals didn't load</h2>
      <p>Something went wrong fetching the feed. Try again.</p>
      <button type="button" className="sx-dbtn sx-dter" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

export function FeedLoadingSkeleton({
  count = 4,
}: {
  count?: number;
}): JSX.Element {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="sx-skel-card" aria-hidden="true">
          <div className="sx-skel-score" />
          <div className="sx-skel-main">
            <div className="sx-skel-line sx-w30" />
            <div className="sx-skel-line sx-w85" />
            <div className="sx-skel-line sx-w60" />
            <div className="sx-skel-pills">
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="sx-skel-right">
            <div className="sx-skel-btn" />
          </div>
        </div>
      ))}
      <p className="sx-loading-note" role="status">
        Loading signals
      </p>
    </div>
  );
}
